import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Headphones, Search, Clock, Send, Sparkles, X, MessageCircle,
  Settings as SettingsIcon, AlertCircle, User as UserIcon, Package,
  Plus, BarChart3, StickyNote, CheckCircle2, Bike,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { pedirExclusao } from '@/lib/confirmarExclusao'

// =================================================
// SAC — Atendimento / pós-venda interno
// =================================================

const STATUS_META: Record<string, { label: string; cor: string }> = {
  aberto: { label: 'Aberto', cor: 'bg-blue-100 text-blue-800' },
  em_atendimento: { label: 'Em atendimento', cor: 'bg-indigo-100 text-indigo-800' },
  aguardando: { label: 'Em espera', cor: 'bg-amber-100 text-amber-800' },
  aguardando_cliente: { label: 'Aguardando cliente', cor: 'bg-amber-100 text-amber-800' },
  resolvido_loja: { label: 'Resolvido na loja', cor: 'bg-teal-100 text-teal-800' },
  resolvido: { label: 'Resolvido', cor: 'bg-emerald-100 text-emerald-800' },
  finalizado: { label: 'Finalizado', cor: 'bg-ink-200 text-ink-800' },
  fechado: { label: 'Fechado', cor: 'bg-ink-100 text-ink-700' },
}
const TIPO_META: Record<string, { label: string; cor: string }> = {
  reclamacao: { label: 'Reclamação', cor: 'bg-red-50 text-red-700' },
  retorno: { label: 'Retorno', cor: 'bg-cyan-50 text-cyan-700' },
  devolucao: { label: 'Devolução', cor: 'bg-violet-50 text-violet-700' },
  troca: { label: 'Troca', cor: 'bg-orange-50 text-orange-700' },
  garantia: { label: 'Garantia', cor: 'bg-amber-50 text-amber-700' },
  duvida: { label: 'Dúvida', cor: 'bg-blue-50 text-blue-700' },
  elogio: { label: 'Elogio', cor: 'bg-emerald-50 text-emerald-700' },
  cancelamento: { label: 'Cancelamento', cor: 'bg-rose-50 text-rose-700' },
  outro: { label: 'Outro', cor: 'bg-ink-100 text-ink-700' },
}
const CANAL_META: Record<string, { label: string; cor: string }> = {
  telefone: { label: 'Telefone', cor: 'bg-sky-100 text-sky-800' },
  whatsapp: { label: 'WhatsApp', cor: 'bg-emerald-100 text-emerald-800' },
  email: { label: 'E-mail', cor: 'bg-blue-100 text-blue-800' },
  balcao: { label: 'Balcão', cor: 'bg-violet-100 text-violet-800' },
  loja: { label: 'Loja', cor: 'bg-amber-100 text-amber-800' },
  manual: { label: 'Manual', cor: 'bg-ink-100 text-ink-700' },
}

function EditarReclamacao({ data, onClose, onSalvo }: { data: any; onClose: () => void; onSalvo: () => void }) {
  const [tipo, setTipo] = useState(data.tipo || 'reclamacao')
  const [prioridade, setPrioridade] = useState(data.prioridade || 'normal')
  const [status, setStatus] = useState(data.status || 'aberto')
  const [modeloBike, setModeloBike] = useState(data.modelo_bike || '')
  const [motivo, setMotivo] = useState(data.motivo || '')
  const [valor, setValor] = useState(data.valor_envolvido != null ? String(data.valor_envolvido) : '')
  const [impacto, setImpacto] = useState(!!data.impacto_reputacao)
  const salvar = useMutation({
    mutationFn: () => api.put(`/sac/atendimentos/${data.id}`, {
      tipo, prioridade, status, modelo_bike: modeloBike || null, motivo: motivo || null,
      valor_envolvido: valor === '' ? null : Number(valor),
      impacto_reputacao: impacto,
    }),
    onSuccess: () => { toast.success('Reclamação atualizada'); onSalvo() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-ink-900">Editar reclamação</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-ink-500 uppercase">Tipo</label>
              <select value={tipo} onChange={e => setTipo(e.target.value)} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm">
                {Object.entries(TIPO_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-500 uppercase">Prioridade</label>
              <select value={prioridade} onChange={e => setPrioridade(e.target.value)} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm">
                <option value="baixa">Baixa</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm">
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Modelo da bike</label>
            <input value={modeloBike} onChange={e => setModeloBike(e.target.value)} placeholder="Ex: Caloi Elite Carbon 2024"
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Motivo / descrição</label>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm resize-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Valor envolvido (R$)</label>
            <input type="number" step="0.01" min="0" value={valor} onChange={e => setValor(e.target.value)}
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
            <input type="checkbox" checked={impacto} onChange={e => setImpacto(e.target.checked)} /> Impacta reputação
          </label>
          <button onClick={() => salvar.mutate()} disabled={salvar.isPending}
            className="w-full btn-primary disabled:opacity-50">
            {salvar.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

const TIPO_OPCOES = ['reclamacao', 'retorno', 'devolucao', 'troca', 'garantia', 'duvida', 'elogio', 'cancelamento', 'outro']
const CANAL_OPCOES = ['telefone', 'whatsapp', 'email', 'balcao', 'loja', 'manual']

export default function SAC() {
  const [filtroStatus, setFiltroStatus] = useState<string>('aberto')
  const [filtroCanal, setFiltroCanal] = useState<string>('')
  const [filtroTipo, setFiltroTipo] = useState<string>('')
  const [busca, setBusca] = useState('')
  const [selecionado, setSelecionado] = useState<number | null>(null)
  const [novo, setNovo] = useState(false)
  const [verRelatorio, setVerRelatorio] = useState(false)

  const { data: contadores } = useQuery<any>({
    queryKey: ['sac-contadores'],
    queryFn: async () => (await api.get('/sac/contadores')).data,
    refetchInterval: 30000,
  })

  const { data: atendimentos = [], isLoading } = useQuery<any[]>({
    queryKey: ['sac-atendimentos', filtroStatus, filtroCanal, filtroTipo],
    queryFn: async () => {
      const params: any = { limit: 200 }
      if (filtroStatus && filtroStatus !== 'todos') params.status = filtroStatus
      if (filtroCanal) params.canal = filtroCanal
      if (filtroTipo) params.tipo = filtroTipo
      return (await api.get('/sac/atendimentos', { params })).data
    },
    refetchInterval: 30000,
  })

  const filtrados = atendimentos.filter(a => {
    if (!busca) return true
    const q = busca.toLowerCase()
    return (
      (a.cliente_nome || '').toLowerCase().includes(q) ||
      (a.motivo || '').toLowerCase().includes(q) ||
      String(a.pedido_numero || '').includes(q)
    )
  })

  return (
    <div className="p-4 lg:p-6 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Headphones size={22} className="text-violet-600" /> SAC
          </h1>
          <p className="text-xs text-ink-500">Atendimento e pós-venda — reclamações, trocas, garantias e dúvidas</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setVerRelatorio(v => !v)}
            className={`text-xs px-3 py-1.5 rounded border flex items-center gap-1.5 ${
              verRelatorio ? 'bg-violet-600 text-white border-violet-600' : 'border-ink-200 hover:bg-ink-50'
            }`}>
            <BarChart3 size={12} /> Relatório
          </button>
          <Link to="/sac/configuracao"
            className="text-xs px-3 py-1.5 rounded border border-ink-200 hover:bg-ink-50 flex items-center gap-1.5">
            <SettingsIcon size={12} /> Config Sofia
          </Link>
          <button onClick={() => setNovo(true)}
            className="btn-primary text-xs flex items-center gap-1.5">
            <Plus size={13} /> Nova reclamação
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KCard label="Abertos" valor={contadores?.abertos || 0} cor="blue" />
        <KCard label="SLA vencido" valor={contadores?.sla_vencido || 0} cor="red" />
        <KCard label="Urgentes" valor={contadores?.urgentes || 0} cor="orange" />
        <KCard label="Resolvidos hoje" valor={contadores?.resolvidos_hoje || 0} cor="emerald" />
      </div>

      {verRelatorio ? (
        <Relatorio />
      ) : (
        <>
          {/* Filtros */}
          <div className="bg-white border border-ink-200 rounded-lg p-3 space-y-2">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
                <input className="input pl-7 text-xs h-8" placeholder="Cliente, motivo, pedido..."
                  value={busca} onChange={e => setBusca(e.target.value)}
                  onKeyDown={enterAbrePrimeiro(filtrados, a => setSelecionado(a.id))} />
              </div>
              <select className="input text-xs h-8 w-40" value={filtroCanal} onChange={e => setFiltroCanal(e.target.value)}>
                <option value="">Todos os canais</option>
                {CANAL_OPCOES.map(c => <option key={c} value={c}>{CANAL_META[c].label}</option>)}
              </select>
              <select className="input text-xs h-8 w-40" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                <option value="">Todos os tipos</option>
                {TIPO_OPCOES.map(t => <option key={t} value={t}>{TIPO_META[t].label}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap gap-1">
              {['aberto', 'em_atendimento', 'aguardando', 'aguardando_cliente', 'resolvido_loja', 'resolvido', 'finalizado', 'todos'].map(s => (
                <button key={s} onClick={() => setFiltroStatus(s)}
                  className={`text-[11px] px-2.5 py-1 rounded font-medium ${
                    filtroStatus === s ? 'bg-violet-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
                  }`}>
                  {s === 'todos' ? 'Todos' : (STATUS_META[s]?.label || s)}
                </button>
              ))}
            </div>
          </div>

          {/* Conteudo principal — lista esquerda + detalhe direita (split) */}
          <div className="bg-white border border-ink-200 rounded-lg overflow-hidden flex" style={{ height: 'calc(100vh - 18rem)', minHeight: 400 }}>
            {/* Lista */}
            <div className={`${selecionado ? 'hidden md:flex w-96' : 'flex w-full md:w-96'} flex-col border-r border-ink-200`}>
              <div className="overflow-y-auto flex-1">
                {isLoading ? (
                  <div className="p-6 text-center text-ink-400 text-xs">Carregando...</div>
                ) : filtrados.length === 0 ? (
                  <div className="p-10 text-center text-ink-400 text-xs">
                    <Headphones size={28} className="mx-auto mb-2 opacity-40" />
                    <div className="mb-1">Nenhum atendimento.</div>
                    <button onClick={() => setNovo(true)} className="text-violet-600 hover:underline text-[11px]">
                      + Abrir uma nova reclamação
                    </button>
                  </div>
                ) : filtrados.map(a => (
                  <button key={a.id} onClick={() => setSelecionado(a.id)}
                    className={`w-full text-left border-b border-ink-100 p-3 hover:bg-blue-50/50 ${
                      selecionado === a.id ? 'bg-blue-50' : ''
                    }`}>
                    <div className="flex items-start gap-1.5 mb-1 flex-wrap">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${CANAL_META[a.canal_origem]?.cor || 'bg-ink-100'}`}>
                        {CANAL_META[a.canal_origem]?.label || a.canal_origem}
                      </span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${TIPO_META[a.tipo]?.cor || 'bg-ink-50'}`}>
                        {TIPO_META[a.tipo]?.label || a.tipo}
                      </span>
                      {a.prioridade === 'urgente' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-600 text-white font-bold">URGENTE</span>
                      )}
                      {a.sla_vencido && a.status !== 'resolvido' && (
                        <AlertCircle size={11} className="text-red-600 flex-shrink-0" />
                      )}
                    </div>
                    <div className="text-xs font-medium text-ink-900 truncate">
                      {a.cliente_nome || 'Sem cliente'}
                    </div>
                    {a.produto_titulo && (
                      <div className="text-[11px] text-ink-700 truncate mt-0.5">
                        <Package size={9} className="inline mr-1 text-ink-400" />
                        {a.produto_titulo}
                        {a.qtd_itens > 1 && <span className="text-ink-400"> +{a.qtd_itens - 1}</span>}
                      </div>
                    )}
                    <div className="text-[11px] text-ink-500 truncate mt-0.5">
                      {a.motivo || 'Sem motivo'}
                    </div>
                    <div className="flex items-center justify-between mt-1 gap-1">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${STATUS_META[a.status]?.cor}`}>
                        {STATUS_META[a.status]?.label || a.status}
                      </span>
                      <span className="text-[10px] text-ink-400 truncate">
                        {a.valor_envolvido ? `${formatMoney(a.valor_envolvido)} · ` : ''}
                        {a.pedido_numero ? `#${a.pedido_numero} · ` : ''}
                        {formatRel(a.criado_em)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Detalhe */}
            <div className={`${selecionado ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-w-0`}>
              {selecionado ? (
                <Detalhe atendimentoId={selecionado} onClose={() => setSelecionado(null)} />
              ) : (
                <div className="flex-1 flex items-center justify-center text-ink-400 text-sm bg-ink-50/50">
                  <div className="text-center px-4">
                    <MessageCircle size={36} className="mx-auto mb-2 opacity-30" />
                    <div className="text-sm">Selecione um atendimento</div>
                    <div className="text-[11px] text-ink-400 mt-1">Lista no painel da esquerda</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {novo && <NovaReclamacao onClose={() => setNovo(false)} onCriado={(id) => { setNovo(false); setVerRelatorio(false); setSelecionado(id) }} />}
    </div>
  )
}

function KCard({ label, valor, cor }: any) {
  const cores: any = {
    blue: 'bg-blue-50 text-blue-900 border-blue-200',
    red: 'bg-red-50 text-red-900 border-red-200',
    orange: 'bg-orange-50 text-orange-900 border-orange-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  }
  return (
    <div className={`border rounded p-1.5 text-center ${cores[cor] || cores.blue}`}>
      <div className="text-[9px] uppercase font-semibold opacity-70 truncate">{label}</div>
      <div className="text-base font-bold font-mono">{valor}</div>
    </div>
  )
}

function formatRel(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return 'agora'
  if (diff < 3600) return `${Math.floor(diff / 60)}min`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  return `${Math.floor(diff / 86400)}d`
}

// =================================================
// NOVA RECLAMAÇÃO — modal de abertura
// =================================================

function NovaReclamacao({ onClose, onCriado }: { onClose: () => void, onCriado: (id: number) => void }) {
  const [buscaCli, setBuscaCli] = useState('')
  const [cliente, setCliente] = useState<any>(null)
  const [pedidoId, setPedidoId] = useState<number | null>(null)
  const [canal, setCanal] = useState('telefone')
  const [tipo, setTipo] = useState('reclamacao')
  const [prioridade, setPrioridade] = useState('normal')
  const [modeloBike, setModeloBike] = useState('')
  const [motivo, setMotivo] = useState('')

  const { data: clientes = [] } = useQuery<any[]>({
    queryKey: ['sac-busca-cliente', buscaCli],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { q: buscaCli, tipo: 'cliente', limit: 8 } })).data,
    enabled: buscaCli.length >= 2 && !cliente,
  })

  const { data: pedidos = [] } = useQuery<any[]>({
    queryKey: ['sac-pedidos-cli', cliente?.id],
    queryFn: async () => (await api.get('/vendas', { params: { cliente_id: cliente.id, limit: 20 } })).data,
    enabled: !!cliente,
  })

  const classificar = useMutation({
    mutationFn: async () => (await api.post('/sac/classificar', { texto: motivo })).data,
    onSuccess: (r: any) => { setTipo(r.tipo); setPrioridade(r.prioridade); toast.success('Sofia classificou') },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Sofia indisponível'),
  })

  const criar = useMutation({
    mutationFn: async () => (await api.post('/sac/atendimentos', {
      canal_origem: canal,
      cliente_id: cliente?.id || null,
      pedido_id: pedidoId,
      tipo, prioridade, motivo, modelo_bike: modeloBike || null,
    })).data,
    onSuccess: (r: any) => { toast.success('Reclamação aberta'); onCriado(r.id) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao abrir'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-ink-200 p-3">
          <h2 className="font-bold text-sm flex items-center gap-2"><Plus size={15} /> Nova reclamação</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-ink-100"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Cliente */}
          <div>
            <label className="text-xs font-medium text-ink-600">Cliente</label>
            {cliente ? (
              <div className="flex items-center justify-between bg-ink-50 border border-ink-200 rounded px-2 py-1.5 mt-1">
                <span className="text-xs">{cliente.nome}</span>
                <button onClick={() => { setCliente(null); setPedidoId(null) }} className="text-ink-400 hover:text-red-600"><X size={13} /></button>
              </div>
            ) : (
              <div className="relative mt-1">
                <input className="input text-xs h-8 w-full" placeholder="Buscar cliente (nome/documento)..."
                  value={buscaCli} onChange={e => setBuscaCli(e.target.value)}
                  onKeyDown={enterAbrePrimeiro(clientes, c => { setCliente(c); setBuscaCli('') })} />
                {clientes.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 bg-white border border-ink-200 rounded shadow-lg mt-0.5 max-h-44 overflow-y-auto">
                    {clientes.map(c => (
                      <button key={c.id} onClick={() => { setCliente(c); setBuscaCli('') }}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b border-ink-100">
                        {c.nome} {c.documento && <span className="text-ink-400">· {c.documento}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pedido vinculado */}
          {cliente && pedidos.length > 0 && (
            <div>
              <label className="text-xs font-medium text-ink-600">Pedido (opcional)</label>
              <select className="input text-xs h-8 w-full mt-1" value={pedidoId ?? ''} onChange={e => setPedidoId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Sem pedido vinculado</option>
                {pedidos.map(p => (
                  <option key={p.id} value={p.id}>#{p.numero} · {formatMoney(p.total)} · {p.status}</option>
                ))}
              </select>
            </div>
          )}

          {/* Canal + tipo + prioridade */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-medium text-ink-600">Canal</label>
              <select className="input text-xs h-8 w-full mt-1" value={canal} onChange={e => setCanal(e.target.value)}>
                {CANAL_OPCOES.map(c => <option key={c} value={c}>{CANAL_META[c].label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-600">Tipo</label>
              <select className="input text-xs h-8 w-full mt-1" value={tipo} onChange={e => setTipo(e.target.value)}>
                {TIPO_OPCOES.map(t => <option key={t} value={t}>{TIPO_META[t].label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-600">Prioridade</label>
              <select className="input text-xs h-8 w-full mt-1" value={prioridade} onChange={e => setPrioridade(e.target.value)}>
                <option value="baixa">Baixa</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>

          {/* Modelo da bike */}
          <div>
            <label className="text-xs font-medium text-ink-600">Modelo da bike</label>
            <input className="input text-xs h-8 w-full mt-1" placeholder="Ex: Caloi Elite Carbon 2024"
              value={modeloBike} onChange={e => setModeloBike(e.target.value)} />
          </div>

          {/* Motivo */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-ink-600">O que o cliente relatou?</label>
              <button onClick={() => classificar.mutate()} disabled={!motivo.trim() || classificar.isPending}
                className="text-[10px] px-2 py-0.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 border border-violet-300 flex items-center gap-1 disabled:opacity-50">
                <Sparkles size={10} /> {classificar.isPending ? '...' : 'Sofia classifica'}
              </button>
            </div>
            <textarea className="input w-full text-xs min-h-[80px] mt-1" placeholder="Descreva a reclamação / solicitação..."
              value={motivo} onChange={e => setMotivo(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-ink-200 p-3">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-ink-200 hover:bg-ink-50">Cancelar</button>
          <button onClick={() => criar.mutate()} disabled={!motivo.trim() || criar.isPending}
            className="btn-primary text-xs">{criar.isPending ? 'Abrindo...' : 'Abrir atendimento'}</button>
        </div>
      </div>
    </div>
  )
}

// =================================================
// DETALHE — chat + ações
// =================================================

function Detalhe({ atendimentoId, onClose }: { atendimentoId: number, onClose: () => void }) {
  const qc = useQueryClient()
  const [texto, setTexto] = useState('')
  const [notaInterna, setNotaInterna] = useState(false)
  const [resolvendo, setResolvendo] = useState(false)
  const [editando, setEditando] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery<any>({
    queryKey: ['sac-atendimento', atendimentoId],
    queryFn: async () => (await api.get(`/sac/atendimentos/${atendimentoId}`)).data,
    refetchInterval: 15000,
  })

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [data?.mensagens?.length])

  const invalida = () => {
    qc.invalidateQueries({ queryKey: ['sac-atendimento', atendimentoId] })
    qc.invalidateQueries({ queryKey: ['sac-atendimentos'] })
    qc.invalidateQueries({ queryKey: ['sac-contadores'] })
  }

  const enviarMut = useMutation({
    mutationFn: async () => (await api.post(`/sac/atendimentos/${atendimentoId}/mensagem`, {
      texto, canal: data?.canal_origem || 'interno', interna: notaInterna,
    })).data,
    onSuccess: () => { setTexto(''); invalida() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao registrar'),
  })

  const escalarMut = useMutation({
    mutationFn: async () => (await api.post(`/sac/atendimentos/${atendimentoId}/escalar`)).data,
    onSuccess: () => { toast.success('Marcado como urgente'); invalida() },
  })

  const sugerirMut = useMutation({
    mutationFn: async () => (await api.post(`/sac/atendimentos/${atendimentoId}/sugerir`)).data,
    onSuccess: (r: any) => { setTexto(r.sugestao); setNotaInterna(false); toast.success('Sofia sugeriu — revise antes de enviar') },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Sofia indisponível'),
  })

  const excluirMut = useMutation({
    mutationFn: async () => (await api.delete(`/sac/atendimentos/${atendimentoId}`)).data,
    onSuccess: () => { toast.success('Reclamação excluída'); invalida(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  if (isLoading || !data) return <div className="p-6 text-center text-ink-400">Carregando...</div>

  const fechado = data.status === 'resolvido' || data.status === 'fechado' || data.status === 'finalizado'

  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-ink-200 p-3 flex items-start gap-3">
        <button onClick={onClose} className="lg:hidden p-1 rounded hover:bg-ink-100"><X size={16} /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${CANAL_META[data.canal_origem]?.cor || 'bg-ink-100'}`}>
              {CANAL_META[data.canal_origem]?.label || data.canal_origem}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${TIPO_META[data.tipo]?.cor}`}>
              {TIPO_META[data.tipo]?.label || data.tipo}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_META[data.status]?.cor}`}>
              {STATUS_META[data.status]?.label || data.status}
            </span>
            {data.prioridade === 'urgente' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white font-bold">URGENTE</span>
            )}
          </div>
          <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
            <UserIcon size={12} className="text-ink-400" />
            {data.cliente_nome || 'Sem cliente'}
            {data.pedido_numero && (
              <Link to={`/vendas`} className="text-violet-600 hover:underline text-xs flex items-center gap-1">
                <Package size={10} /> Pedido #{data.pedido_numero}
              </Link>
            )}
            {data.valor_envolvido != null && (
              <span className="text-xs font-mono text-emerald-700">{formatMoney(data.valor_envolvido)}</span>
            )}
          </div>
          {data.produto_titulo && (
            <div className="text-[11px] text-ink-600 mt-0.5 flex items-center gap-1">
              <Package size={10} className="text-ink-400" />
              <span className="truncate">{data.produto_titulo}</span>
              {data.qtd_itens > 1 && <span className="text-ink-400">+{data.qtd_itens - 1}</span>}
            </div>
          )}
          {data.modelo_bike && (
            <div className="text-[11px] text-ink-700 mt-0.5 flex items-center gap-1">
              <Bike size={11} className="text-ink-400" />
              <span className="truncate font-medium">{data.modelo_bike}</span>
            </div>
          )}
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {data.atendente_nome && (
              <span className="text-[11px] text-ink-500">Resp.: {data.atendente_nome}</span>
            )}
            {data.prazo_resposta_em && !fechado && (
              <span className={`text-[11px] ${data.sla_vencido ? 'text-red-600' : 'text-ink-500'}`}>
                <Clock size={9} className="inline mr-1" />
                SLA: {new Date(data.prazo_resposta_em).toLocaleString('pt-BR')}{data.sla_vencido && ' (vencido)'}
              </span>
            )}
          </div>
          {fechado && data.resolucao && (
            <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
              <CheckCircle2 size={11} /> {data.desfecho ? `${data.desfecho} · ` : ''}{data.resolucao}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {!fechado && (
            <>
              <button onClick={() => escalarMut.mutate()} disabled={data.prioridade === 'urgente'}
                className="text-[10px] px-2 py-1 rounded bg-orange-100 hover:bg-orange-200 text-orange-800 disabled:opacity-50">
                Escalar
              </button>
              <button onClick={() => setResolvendo(true)}
                className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white">
                Resolver
              </button>
            </>
          )}
          <button onClick={() => setEditando(true)}
            className="text-[10px] px-2 py-1 rounded bg-ink-100 hover:bg-ink-200 text-ink-700">
            Editar
          </button>
          <button onClick={() => pedirExclusao({ descricao: 'o atendimento', onConfirm: () => excluirMut.mutate() })}
            className="text-[10px] px-2 py-1 rounded bg-red-50 hover:bg-red-100 text-red-700">
            Excluir
          </button>
        </div>
      </div>
      {editando && (
        <EditarReclamacao data={data} onClose={() => setEditando(false)}
          onSalvo={() => { setEditando(false); invalida() }} />
      )}

      {/* Histórico */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-ink-50">
        {data.motivo && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
            <div className="font-semibold text-amber-900 mb-0.5">Motivo / Contexto</div>
            <div className="text-amber-800 whitespace-pre-wrap">{data.motivo}</div>
          </div>
        )}
        {(data.mensagens || []).map((m: any) => {
          if (m.direcao === 'interna') {
            return (
              <div key={m.id} className="flex justify-center">
                <div className="max-w-md bg-yellow-50 border border-yellow-200 rounded px-3 py-1.5 text-[11px] text-yellow-900">
                  <div className="flex items-center gap-1 font-semibold mb-0.5"><StickyNote size={10} /> Nota interna</div>
                  <div className="whitespace-pre-wrap">{m.texto}</div>
                </div>
              </div>
            )
          }
          return (
            <div key={m.id} className={`flex ${m.direcao === 'saida' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-md rounded-lg px-3 py-2 text-xs shadow-sm ${
                m.direcao === 'saida' ? 'bg-blue-600 text-white' : 'bg-white border border-ink-200'
              }`}>
                <div className="whitespace-pre-wrap">{m.texto}</div>
                <div className={`text-[9px] mt-1 ${m.direcao === 'saida' ? 'text-blue-100' : 'text-ink-500'}`}>
                  {new Date(m.enviado_em).toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          )
        })}
        {(!data.mensagens || data.mensagens.length === 0) && (
          <div className="text-center text-ink-400 text-xs py-8">Nenhuma interação ainda.</div>
        )}
      </div>

      {/* Composer */}
      {!fechado && (
        <div className="border-t border-ink-200 p-3 bg-white">
          <textarea
            className="input w-full text-xs min-h-[60px]"
            placeholder={notaInterna ? 'Nota interna (não vai pro cliente)...' : 'Resposta ao cliente... (Enter envia / Shift+Enter quebra linha)'}
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (texto.trim()) enviarMut.mutate() }
            }}
          />
          <div className="flex justify-between items-center mt-2 gap-2 flex-wrap">
            <label className="text-[11px] text-ink-600 flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={notaInterna} onChange={e => setNotaInterna(e.target.checked)} />
              <StickyNote size={11} /> Nota interna
            </label>
            <div className="flex gap-1">
              <button onClick={() => sugerirMut.mutate()} disabled={sugerirMut.isPending || notaInterna}
                title="Sofia sugere uma resposta no campo acima — você revisa e envia"
                className="text-xs px-2.5 py-1.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 border border-violet-300 flex items-center gap-1 disabled:opacity-50">
                <Sparkles size={11} /> {sugerirMut.isPending ? 'Pensando...' : 'Sugerir (Sofia)'}
              </button>
              <button onClick={() => enviarMut.mutate()} disabled={!texto.trim() || enviarMut.isPending}
                className="btn-primary text-xs flex items-center gap-1">
                {notaInterna ? <StickyNote size={11} /> : <Send size={11} />}
                {notaInterna ? 'Salvar nota' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resolvendo && <ResolverModal atendimentoId={atendimentoId} tipo={data.tipo} onClose={() => setResolvendo(false)} onResolvido={() => { setResolvendo(false); invalida() }} />}
    </div>
  )
}

// =================================================
// RESOLVER — modal de desfecho
// =================================================

function ResolverModal({ atendimentoId, tipo, onClose, onResolvido }: { atendimentoId: number, tipo: string, onClose: () => void, onResolvido: () => void }) {
  const [desfecho, setDesfecho] = useState(tipo === 'elogio' ? 'sem_classificacao' : 'procede')
  const [categoria, setCategoria] = useState('')
  const [resolucao, setResolucao] = useState('')

  const mut = useMutation({
    mutationFn: async () => (await api.post(`/sac/atendimentos/${atendimentoId}/resolver`, {
      desfecho, categoria: categoria || null, resolucao,
    })).data,
    onSuccess: () => { toast.success('Atendimento resolvido'); onResolvido() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao resolver'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-ink-200 p-3">
          <h2 className="font-bold text-sm flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-600" /> Resolver atendimento</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-ink-100"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-ink-600">Desfecho</label>
            <div className="flex gap-1 mt-1">
              {[['procede', 'Procede'], ['improcede', 'Improcede'], ['sem_classificacao', 'N/A']].map(([v, l]) => (
                <button key={v} onClick={() => setDesfecho(v)}
                  className={`text-xs px-3 py-1.5 rounded flex-1 ${desfecho === v ? 'bg-violet-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-ink-600">Solução dada (categoria)</label>
            <select className="input text-xs h-8 w-full mt-1" value={categoria} onChange={e => setCategoria(e.target.value)}>
              <option value="">Selecione...</option>
              <option value="troca">Troca</option>
              <option value="reembolso">Reembolso</option>
              <option value="reenvio">Reenvio</option>
              <option value="reparo">Reparo / garantia</option>
              <option value="orientacao">Orientação / dúvida sanada</option>
              <option value="desconto">Desconto / cortesia</option>
              <option value="sem_acao">Sem ação necessária</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-ink-600">Descrição da resolução</label>
            <textarea className="input w-full text-xs min-h-[70px] mt-1" placeholder="O que foi feito pra resolver..."
              value={resolucao} onChange={e => setResolucao(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-ink-200 p-3">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-ink-200 hover:bg-ink-50">Cancelar</button>
          <button onClick={() => mut.mutate()} disabled={mut.isPending}
            className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white">
            {mut.isPending ? 'Salvando...' : 'Confirmar resolução'}
          </button>
        </div>
      </div>
    </div>
  )
}

// =================================================
// RELATÓRIO — dashboard analítico
// =================================================

function Relatorio() {
  const [dias, setDias] = useState(30)
  const { data, isLoading } = useQuery<any>({
    queryKey: ['sac-relatorio', dias],
    queryFn: async () => (await api.get('/sac/relatorio', { params: { dias } })).data,
  })

  if (isLoading || !data) return <div className="p-10 text-center text-ink-400 text-xs">Carregando relatório...</div>

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-ink-500">Período:</span>
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDias(d)}
            className={`text-[11px] px-2.5 py-1 rounded font-medium ${dias === d ? 'bg-violet-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}>
            {d}d
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <KCard label="Atendimentos" valor={data.total_periodo} cor="blue" />
        <KCard label="Resolvidos" valor={data.resolvidos} cor="emerald" />
        <KCard label="Tempo médio (h)" valor={data.tempo_medio_horas ?? '—'} cor="orange" />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Bloco titulo="Por tipo">
          {data.por_tipo.map((x: any) => (
            <LinhaBar key={x.tipo} label={TIPO_META[x.tipo]?.label || x.tipo} qtd={x.qtd} max={data.por_tipo[0]?.qtd || 1} />
          ))}
        </Bloco>
        <Bloco titulo="Desfecho (resolvidos)">
          {data.por_desfecho.length === 0 ? <Vazio /> : data.por_desfecho.map((x: any) => (
            <LinhaBar key={x.desfecho} label={x.desfecho} qtd={x.qtd} max={Math.max(...data.por_desfecho.map((y: any) => y.qtd))} />
          ))}
        </Bloco>
        <Bloco titulo="Solução dada (categoria)">
          {data.por_categoria.length === 0 ? <Vazio /> : data.por_categoria.map((x: any) => (
            <LinhaBar key={x.categoria} label={x.categoria} qtd={x.qtd} max={data.por_categoria[0]?.qtd || 1} />
          ))}
        </Bloco>
        <Bloco titulo="Produtos mais reclamados">
          {data.produtos_mais_reclamados.length === 0 ? <Vazio /> : data.produtos_mais_reclamados.map((x: any, i: number) => (
            <div key={i} className="flex justify-between text-xs py-0.5 border-b border-ink-100">
              <span className="truncate pr-2">{x.produto}</span>
              <span className="font-mono text-ink-600">{x.qtd}</span>
            </div>
          ))}
        </Bloco>
        <Bloco titulo="Clientes reincidentes">
          {data.clientes_reincidentes.length === 0 ? <Vazio /> : data.clientes_reincidentes.map((x: any) => (
            <div key={x.cliente_id} className="flex justify-between text-xs py-0.5 border-b border-ink-100">
              <span className="truncate pr-2">{x.nome}</span>
              <span className="font-mono text-red-600">{x.qtd}×</span>
            </div>
          ))}
        </Bloco>
      </div>
    </div>
  )
}

function Bloco({ titulo, children }: any) {
  return (
    <div className="bg-white border border-ink-200 rounded-lg p-3">
      <div className="text-xs font-semibold text-ink-700 mb-2">{titulo}</div>
      <div className="space-y-1">{children}</div>
    </div>
  )
}
function Vazio() { return <div className="text-[11px] text-ink-400 py-2">Sem dados no período.</div> }
function LinhaBar({ label, qtd, max }: { label: string, qtd: number, max: number }) {
  const pct = max ? Math.round((qtd / max) * 100) : 0
  return (
    <div className="text-xs">
      <div className="flex justify-between mb-0.5"><span className="truncate pr-2">{label}</span><span className="font-mono text-ink-600">{qtd}</span></div>
      <div className="h-1.5 bg-ink-100 rounded"><div className="h-1.5 bg-violet-500 rounded" style={{ width: `${pct}%` }} /></div>
    </div>
  )
}
